// components
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// hooks
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// types
import { EmployeesTableProps } from './EmployeesTable.types';
import CommunicationDialog from './CommunicationDialog';
import { usePreferredEmployeeActions } from '../hooks/usePreferredEmployeeActions';
import { PreferredEmployee } from 'components/shared/PreferredEmployees.types';
import useConnectEmployeeToFlexMatchRequest from '../hooks/useConnectEmployeeToFlexMatchRequest';

export const EmployeesTable = ({
  selectedRow,
  data,
  isLoading,
  columns,
  rowId,
  isCandidatesTable,
  onClose,
  isOpen = true
}: EmployeesTableProps) => {
  if (!selectedRow || !data || !columns || !rowId) return null;
  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch'
  });
  const { t: tCommon } = useTranslation('common');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });
  const [openCommunicationDialog, setOpenCommunicationDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isPhoneMessage, setIsPhoneMessage] = useState(false);

  const { addEmployeeFor } = usePreferredEmployeeActions();
  const { mutateAsync: connectEmployeeToFlexMatchRequest } =
    useConnectEmployeeToFlexMatchRequest({ isWebSocketUpdate: true });

  const handleAddEmployee = useMemo(
    () => addEmployeeFor(selectedRow, isOpen),
    [addEmployeeFor, selectedRow, isOpen]
  );

  const handleOpenCommunicationDialog = (row, isPhoneMessage = false) => {
    setSelectedEmployee(row);
    setIsPhoneMessage(isPhoneMessage);
    setOpenCommunicationDialog(true);
  };

  const handleCloseCommunicationDialog = () => {
    setOpenCommunicationDialog(false);
    setSelectedEmployee(null);
  };

  const handleConnectEmployee = async (row) => {
    if (!row) return;

    const { employeeId, workAgreementId } = row;

    await connectEmployeeToFlexMatchRequest(
      {
        taskId: selectedRow.taskId,
        body: {
          formdata: {
            actions: 'connect',
            employeeId,
            workAgreementId
          }
        }
      },
      {
        onSuccess: () => {
          onClose?.();
        }
      }
    );
  };

  return (
    <>
      <TableCRUDLayout
        columns={columns ?? []}
        rows={data ?? []}
        getRowId={rowId}
        dynamicHeight
        editable={false}
        loading={isLoading}
        pagination
        pageSize={paginationModel.pageSize}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={({ pageSize }) =>
          setPaginationModel({ ...paginationModel, pageSize })
        }
        actionsThreshold={4}
        customActions={(_, row) => [
          {
            tooltipTitle: tCommon('email'),
            icon: 'envelope',
            onClick: () => handleOpenCommunicationDialog(row),
            hidden: !row?.askedTaskId || !row?.comEmail
          },
          {
            tooltipTitle: tCommon('messages'),
            icon: 'comments',
            onClick: () => handleOpenCommunicationDialog(row, true),
            hidden: !row?.askedTaskId || !row?.comPhone
          },
          {
            tooltipTitle: t('preference'),
            icon: 'star-outline',
            onClick: (_id, _row, _func, event) => {
              event?.stopPropagation();
              handleAddEmployee(_row as PreferredEmployee);
            },
            hidden: isCandidatesTable
          },
          {
            tooltipTitle: t('link'),
            icon: 'link',
            onClick: () => handleConnectEmployee(row)
          }
        ]}
      />

      <CommunicationDialog
        isOpen={openCommunicationDialog}
        onClose={handleCloseCommunicationDialog}
        selectedEmployee={selectedEmployee}
        selectedRow={selectedRow}
        isPhoneMessage={isPhoneMessage}
      />
    </>
  );
};
